import { Box, SxProps, Typography } from '@mui/material';
import { useIntl } from 'react-intl';

import { DataGrid, DataGridLayout, WorkflowMessage } from '@verantos-dev/shared-components';
import { CohortDefinitionStatus, type CohortSubjectDetail } from '@verantos-dev/types-cohorts';

import { useCohortSubjectList } from './lib/hooks/useCohortSubjectList';

const workflowMessageSx: SxProps = { width: '100%', height: '348px' };

export const CohortSubjectsList = ({
  projectId,
  cohortId,
}: {
  projectId: string;
  cohortId: string;
}) => {
  const intl = useIntl();

  const {
    theme,
    latestCohortStatus,
    useFetchSubjectListData,
    columnDefinition,
    subjectsData,
    subjectsError,
    subjectWorkflowMessages,
  } = useCohortSubjectList(projectId, cohortId);

  if (latestCohortStatus === CohortDefinitionStatus.DRAFT) {
    return (
      <WorkflowMessage
        id="CohortSubjects-draft"
        title={subjectWorkflowMessages.draftTitle}
        message={subjectWorkflowMessages.draftMessage}
        sx={workflowMessageSx}
      />
    );
  }
  if (
    latestCohortStatus === CohortDefinitionStatus.PROCESSING &&
    (!subjectsError || !subjectsData?.data)
  ) {
    return (
      <WorkflowMessage
        id="CohortSubjects-processing"
        title={subjectWorkflowMessages.processingTitle}
        message={subjectWorkflowMessages.processingMessage}
        sx={workflowMessageSx}
      />
    );
  }
  if (subjectsData?.data.length === 0) {
    return (
      <WorkflowMessage
        id="CohortSubjects-processed-no-results"
        title={subjectWorkflowMessages.processedNoDataTitle}
        message={subjectWorkflowMessages.processedNoDataMessage}
        sx={workflowMessageSx}
      />
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="h5">
        {intl.formatMessage({ id: 'projects.cohortBuilder.cohortSubjectsPageTab.title' })}
      </Typography>

      <DataGridLayout
        id="subject-list-wrapper"
        sx={{
          width: '100%',
          // Relaxed from `md` so the table card can shrink with the
          // Research Assistant sidebar open; the DataGrid scrolls its
          // own columns internally below this width.
          minWidth: theme.breakpoints.values.sm,
          minHeight: 250,
          maxHeight: 630,
          marginTop: theme.spacing(4),
        }}
      >
        <DataGrid
          id="subject-list"
          columns={columnDefinition}
          getRowId={(row: CohortSubjectDetail) => row.subjectId}
          mode="server"
          useFetchDataGridData={useFetchSubjectListData}
          getRowCount={(data) => data.pagination.total}
          pagination
          loadOnFetch
          disableSearchParamSync
          initialPageSize={500}
          pageSizeOptions={[25, 50, 100, 250, 500]}
        />
      </DataGridLayout>
    </Box>
  );
};
